import axios from 'axios';
import moment from 'moment-timezone'; // Assuming moment is available for date formatting

// Enhanced formatDate function with timezone support
const formatDate = (dateStr) => {
  // If date is already in MM/DD/YYYY format, return as is
  if (typeof dateStr === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr)) {
    return dateStr;
  }
  
  try {
    // Use moment to parse and format with timezone awareness
    return moment(dateStr).format('MM/DD/YYYY');
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateStr; // Return original if parsing fails
  }
};

// No need for getApiBaseUrl helper, assuming axios.defaults.baseURL is set globally

const namespaced = true;

const state = {
  // New structure: { 'YYYY-MM-DD': { 'Doctor Name': ['slot1', 'slot2'], ... }, ... }
  availabilityData: {},
  isLoading: false,
  error: null,
  siteInfo: null,  // New state to hold site information
};

const mutations = {
  SET_LOADING(state, isLoading) {
    state.isLoading = isLoading;
  },
  SET_AVAILABILITY_BATCH(state, availabilityMap) {
    // Deep merge new data with existing data for the nested structure
    for (const dateISO in availabilityMap) {
        if (!state.availabilityData[dateISO]) {
            // If date doesn't exist, create it
            state.availabilityData = { ...state.availabilityData, [dateISO]: {} };
        }
        // Merge the doctor availability for the specific date
        state.availabilityData[dateISO] = {
            ...state.availabilityData[dateISO],
            ...availabilityMap[dateISO],
        };
    }
  },
  CLEAR_AVAILABILITY(state) {
    state.availabilityData = {};
  },
  SET_ERROR(state, error) {
    state.error = error;
    if (error) {
      console.error("STORE: Calendar error:", error);
    }
  },
  SET_SITE_INFO(state, siteInfo) {
    state.siteInfo = siteInfo;
  },
};

const actions = {
  // Action now expects payload: { siteId: '...', dates: [...] }
  async fetchAvailability({ commit, rootState }, payload) {
    const { siteId, dates } = payload;

    // Validate payload
    if (!siteId) {
        commit('SET_ERROR', 'Site ID is missing in payload. Cannot fetch availability.');
        return;
    }
    if (!dates || dates.length === 0) {
      return;
    }

    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    // Use relative path, assuming axios.defaults.baseURL is set
    const endpoint = '/get-appointment-availability';
    const formattedCurrentDate = formatDate(new Date()); // Today's date MM/DD/YYYY

    // --- MODIFIED: Single API call for date range --- 
    const requestPayload = {
        site_id: siteId,
        dates: dates.map(d => formatDate(d)), // Format dates to MM/DD/YYYY
        current_date: formattedCurrentDate // Send current date for backend logic (already MM/DD/YYYY)
    };
    console.log(`STORE: Fetching availability for site ${siteId}`);
    const headers = {
        'Content-Type': 'application/json',
    };

    try {
      // Make a single POST request
      const response = await axios.post(endpoint, requestPayload, { headers });
      
      // This map will hold the final nested structure for the batch
      const availabilityMapForBatch = {};

      // Check if the expected structure exists
      if (response.data && response.data.availability && typeof response.data.availability === 'object') {
        const availabilityResults = response.data.availability;

        // Process the results for each date requested
        dates.forEach(dateISO => {
            availabilityMapForBatch[dateISO] = {}; // Initialize date entry
            
            // Get result using MM/DD/YYYY format (backend format)
            const mmddyyyy = formatDate(dateISO); // Convert YYYY-MM-DD to MM/DD/YYYY
            const resultForDate = availabilityResults[mmddyyyy]; // Get data for this specific date

            if (resultForDate && resultForDate.exactMatch && Array.isArray(resultForDate.exactMatch)) {
                // Process the exactMatch array for this date
                resultForDate.exactMatch.forEach(doctorMatch => {
                    const doctorName = doctorMatch.doctor;
                    let doctorSlots = [];
                    
                    // Extract time slots from the nested structure
                    if (doctorMatch.availability && doctorMatch.availability.length > 0) {
                        // Find the entry for this date
                        const dateEntry = doctorMatch.availability.find(avail => 
                            avail.date === mmddyyyy && Array.isArray(avail.timeSlots)
                        );
                        
                        if (dateEntry && dateEntry.timeSlots) {
                            doctorSlots = dateEntry.timeSlots;
                        }
                    }

                    if (doctorName && doctorSlots.length > 0) {
                        availabilityMapForBatch[dateISO][doctorName] = doctorSlots;
                    }
                });
            }
        });

      } else {
          console.error("STORE: Unexpected API response structure");
          commit('SET_ERROR', 'Received invalid availability data structure from server.');
          // Initialize empty data for requested dates to avoid errors
          dates.forEach(dateISO => {
            availabilityMapForBatch[dateISO] = {}; 
          });
      }

      // Commit the entire nested map for the batch
      commit('SET_AVAILABILITY_BATCH', availabilityMapForBatch);

    } catch (error) {
      console.error("STORE: Error fetching availability:", error.response?.data || error.message);
      commit('SET_ERROR', 'Failed to load availability data.');
    } finally {
      commit('SET_LOADING', false);
    }
  },

  initializeCalendar({ dispatch }) {
    // No need for log here
  },

  // Site info for the public calendar page, by plugin ID. This page has no login, so it uses a small typed route (main-service
  // getPluginSiteInfo) and NEVER the open raw-SQL route (2026-10-19; test: store/__tests__/public-pages-no-open-sql.test.js).
  fetchSiteInfoByPluginId({ commit }, { pluginId }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    return axios.post('/get-plugin-site-info', { pluginId }, { crossdomain: true })
      .then(response => {
        if (response.data && response.data.length > 0) {
          // Format the result for the component
          const siteInfo = {
            siteId: response.data[0].siteID,
            websiteUrl: response.data[0].WebsiteUrl,
            timezone: response.data[0].Timezone || 'America/New_York'
          };
          commit('SET_SITE_INFO', siteInfo);
          return siteInfo;
        } else {
          commit('SET_ERROR', 'Invalid plugin ID or missing timezone setting');
          throw new Error('Invalid plugin ID or missing timezone setting');
        }
      })
      .catch(error => {
        if (error.response && error.response.status === 404) {
          // an unknown plugin id: the same message the page showed when the old query found no row
          commit('SET_ERROR', 'Invalid plugin ID or missing timezone setting');
        } else {
          const errorMessage = error.response?.data?.message || error.message;
          commit('SET_ERROR', `Error fetching plugin info: ${errorMessage}`);
        }
        throw error;
      })
      .finally(() => {
        commit('SET_LOADING', false);
      });
  },
};

const getters = {
  // Updated getter to return the object with doctor availability for a date
  getAvailabilityForDate: (state) => (dateISO) => {
      // Remove all debug logs
      const availability = state.availabilityData[dateISO];
      
      if (!availability) {
          return null;
      }
      
      // Only consider empty if there are no doctor entries with slots
      if (Object.keys(availability).length === 0) {
          return null;
      }
      
      // Enhanced validation: Check if there are any doctors with actual slots
      let hasDoctorsWithSlots = false;
      for (const doctor in availability) {
          // Check that slots exist and are in expected format
          if (availability[doctor] && 
              Array.isArray(availability[doctor]) && 
              availability[doctor].length > 0) {
              // Extra check for valid slot strings
              if (typeof availability[doctor][0] === 'string' && 
                  (availability[doctor][0].toLowerCase().includes('am') || 
                   availability[doctor][0].toLowerCase().includes('pm'))) {
                  hasDoctorsWithSlots = true;
                  break;
              }
          }
      }
      
      if (!hasDoctorsWithSlots) {
          return null;
      }
      
      return availability; // Returns { 'Doctor Name': [...] }
  },
  isLoading: (state) => state.isLoading,
  getError: (state) => state.error,
  getSiteInfo: state => state.siteInfo,
  getSiteTimezone: state => {
    if (state.siteInfo && state.siteInfo.timezone) {
      return state.siteInfo.timezone;
    }
    return 'America/New_York';
  },
};

export default {
  namespaced,
  state,
  mutations,
  actions,
  getters,
}; 